import { useEffect, useRef } from 'react';
import './Clapperboard.css';

interface ClapperboardProps {
  autoClap?: boolean;
  onClapComplete?: () => void;
  scrollProgress?: number; // For scroll-based animation (0 to 1)
}

export default function Clapperboard({ autoClap = true, onClapComplete, scrollProgress }: ClapperboardProps) {
  const stickRef = useRef<HTMLDivElement>(null);
  const hasAutoClappedRef = useRef(false);

  useEffect(() => {
    // Only auto-clap if scrollProgress is not provided (standalone mode)
    if (autoClap && scrollProgress === undefined && stickRef.current && !hasAutoClappedRef.current) {
      // Trigger clap animation on mount
      stickRef.current.classList.add('animate');
      hasAutoClappedRef.current = true;
      
      // Call completion callback after animation
      const timer = setTimeout(() => {
        if (onClapComplete) {
          onClapComplete();
        }
      }, 2000); // Match animation duration

      return () => clearTimeout(timer);
    }
  }, [autoClap, onClapComplete, scrollProgress]);

  // Auto-trigger clap when scrollProgress reaches early threshold (0.1)
  // Only trigger once when crossing the threshold
  useEffect(() => {
    if (
      scrollProgress !== undefined &&
      scrollProgress >= 0.1 &&
      scrollProgress < 0.15 && // Trigger between 0.1 and 0.15 to avoid multiple triggers
      stickRef.current &&
      !hasAutoClappedRef.current
    ) {
      stickRef.current.classList.add('animate');
      hasAutoClappedRef.current = true;
      
      // Remove animate class after animation completes to allow smooth return
      const timer = setTimeout(() => {
        if (stickRef.current) {
          stickRef.current.classList.remove('animate');
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [scrollProgress]);

  const handleClick = () => {
    if (stickRef.current) {
      // Temporarily clear inline transform if scroll-based rotation is active
      if (scrollProgress !== undefined && stickRef.current.style.transform) {
        stickRef.current.style.transform = '';
      }
      // Remove animate class and wait for transition to complete before restarting
      stickRef.current.classList.remove('animate');
      // Use requestAnimationFrame to ensure smooth transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (stickRef.current) {
            stickRef.current.classList.add('animate');
          }
        });
      });
    }
  };

  // Calculate stick rotation based on scroll progress if provided
  // Start closing after scrollProgress 0.25, finish closing by 0.4
  const stickRotation = scrollProgress !== undefined
    ? scrollProgress < 0.25
      ? -10 // Start open - further reduced from -15deg
      : Math.min(0, -10 + (scrollProgress - 0.25) * (10 / 0.15)) // Close from -10deg to 0deg over 0.15 progress (0.25 to 0.4)
    : undefined;

  return (
    <div className="clapperboard-container" onClick={handleClick}>
      <div
        className="clapper-stick"
        ref={stickRef}
        style={
          stickRotation !== undefined
            ? {
                transform: `rotate(${stickRotation}deg)`,
                transition: 'transform 0.1s linear',
                // Override CSS transition when scroll-based rotation is active
              }
            : undefined
        }
      >
        <div className="clapper-stripes"></div>
      </div>
      <div className="clapper-slate">
        <div className="slate-content">
          <h2>A movie of and for the family.</h2>
          <p className="handwritten-highlight">
            <span className="highlight-line highlight-line-1">By You.</span>
            <br />
            <span className="highlight-line highlight-line-2">From Us.</span>
            <br />
            <span className="highlight-line highlight-line-3">For You.</span>
          </p>
        </div>
      </div>
    </div>
  );
}
